import React from 'react';
import styled, { css } from 'styled-components';
// Untyped third-party boundary: react-transition-group ships no bundled types and none are
// installed. It resolves to an untyped CJS module (so `declare module` augmentation is
// rejected), so we suppress the missing-declaration error here; the imports resolve as `any`.
// @ts-expect-error -- no type declarations for react-transition-group (TS7016)
import { TransitionGroup, Transition } from 'react-transition-group';

import { FjPortal } from '../fj-portal';

const enterTimeoutMs = 300;
const exitTimeoutMs = 250;

type TransitionState = 'entering' | 'entered' | 'exiting' | 'exited' | 'unmounted';

const Wrapper = styled(FjPortal)<{ width: string }>`
  position: absolute;
  top: 0;
  right: 0;
  margin: 10px;
  width: 100%;
  max-width: ${({ width }) => width};
`;

const Animate = styled.div<{ state: TransitionState }>`
  transition-property: opacity, transform;
  transition-duration: 250ms;
  transition-timing-function: ease-in-out;

  ${(props) =>
    props.state === 'entering' &&
    css`
      opacity: 0;
      transform: translateY(-20px);
    `};
  ${(props) =>
    props.state === 'entered' &&
    css`
      opacity: 1;
      transform: translateY(0);
    `};
  ${(props) =>
    props.state === 'exiting' &&
    css`
      opacity: 0;
      transform: translateX(100%);
    `};
  ${(props) =>
    props.state === 'exited' &&
    css`
      opacity: 0;
      transform: translateX(100%);
    `};
`;

interface NotificationTransitionProps {
  children: React.ReactNode;
  [key: string]: unknown;
}

const NotificationTransition = ({ children, ...props }: NotificationTransitionProps) => {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  return (
    <Transition nodeRef={nodeRef} {...props}>
      {(state: TransitionState) => (
        <Animate ref={nodeRef} state={state}>
          {children}
        </Animate>
      )}
    </Transition>
  );
};

interface NotificationManagerProps {
  children: React.ReactNode;
  className?: string;
  width?: string;
}

export const NotificationManager = ({
  children,
  className = '',
  width = '100%',
}: NotificationManagerProps) => {
  const notifications = React.Children.map(children, (child) => (
    <NotificationTransition
      appear
      timeout={{
        enter: enterTimeoutMs,
        exit: exitTimeoutMs,
      }}
      key={(child as React.ReactElement<{ id: string }>).props.id}
    >
      {child}
    </NotificationTransition>
  ));

  return (
    <Wrapper className={className} width={width}>
      <TransitionGroup>{notifications}</TransitionGroup>
    </Wrapper>
  );
};

export default NotificationManager;
